import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { createApiClient } from "../utils/apiClient";
import { useUser } from "../utils/userContext";
import FeatureGate from "../utils/featureGate";
import Toast from "./Toast";
import styles from "./AuditHistory.module.css";

// ── Types ──────────────────────────────────────────────

interface AuditLogItem {
  id: number;
  entityType: string | null;
  entityId: string | null;
  entityName: string | null;
  entityLabel?: string | null;
  action: string | null;
  actionLabel?: string | null;
  userId: number | null;
  userName: string | null;
  actorLabel?: string | null;
  changes: string | null;
  changeSummary?: string | null;
  timestamp: string;
}

interface AuditLogsResponse {
  total: number;
  page: number;
  pageSize: number;
  items: AuditLogItem[];
}

// ── Constants ──────────────────────────────────────────

const ENTITY_TYPES = [
  { value: "", label: "All" },
  { value: "User", label: "User" },
  { value: "Client", label: "Client" },
  { value: "TimeEntry", label: "TimeEntry" },
  { value: "Subscription", label: "Subscription" },
  { value: "Invoice", label: "Invoice" },
];

const PAGE_SIZE = 20;
const ENTITY_LABELS: Record<string, string> = {
  UserTenant: "User membership",
  ExpenseLineItem: "Expense line item",
  TimeDetails: "Time entry",
  ProjectInfo: "Prospect",
  ProjectDescription: "Legal description",
};
const FIELD_LABELS: Record<string, string> = {
  RoleId: "Role",
  ExpenseTypeId: "Expense type",
  TimeId: "Time entry",
  JobTypeId: "Job type",
  ProjectId: "Prospect",
  ProjectDescriptionId: "Legal description",
  UserId: "User",
  TenantId: "Tenant",
  UpdatedAt: "Updated at",
};

// ── Helpers ────────────────────────────────────────────

const formatTimestamp = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const actionClass = (action: string | null): string => {
  switch (action?.toLowerCase()) {
    case "created":
      return styles["action-created"];
    case "updated":
      return styles["action-updated"];
    case "deleted":
      return styles["action-deleted"];
    default:
      return styles["action-default"];
  }
};

const maskInternalValue = (value: unknown): string => {
  const text = String(value ?? "");
  return text.replace(
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
    "[guid]",
  );
};

const humanizeIdentifier = (value: string | null | undefined): string => {
  if (!value) return "—";
  if (ENTITY_LABELS[value]) return ENTITY_LABELS[value];
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
};

const humanizeField = (value: string): string => FIELD_LABELS[value] || humanizeIdentifier(value);

interface ChangeSummary {
  field: string;
  from: string;
  to: string;
}

const parseChanges = (raw: string | null): ChangeSummary[] | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;

    return Object.entries(parsed).map(([field, value]: [string, any]) => {
      if (
        value &&
        typeof value === "object" &&
        ("from" in value || "to" in value)
      ) {
        return {
          field: humanizeField(field),
          from: maskInternalValue(value.from ?? ""),
          to: maskInternalValue(value.to ?? ""),
        };
      }
      if (Array.isArray(value) && value.length >= 2) {
        return {
          field: humanizeField(field),
          from: maskInternalValue(value[0]),
          to: maskInternalValue(value[1]),
        };
      }
      return { field: humanizeField(field), from: "", to: maskInternalValue(value ?? "") };
    });
  } catch {
    return null;
  }
};

// ── Component ──────────────────────────────────────────

const AuditHistoryContent = () => {
  const { token, getToken } = useAuthToken();
  const { user, activeTenantId } = useUser();

  const [items, setItems] = useState<AuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  // Filters
  const [entityType, setEntityType] = useState("");
  const [since, setSince] = useState("");
  const [search, setSearch] = useState("");

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const fetchAudit = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const query: Record<string, string> = {
        page: String(page),
        pageSize: String(PAGE_SIZE),
      };
      if (entityType) query.entityType = entityType;
      if (since) query.since = since;
      if (search) query.entityId = search;

      const data = await api.get<AuditLogsResponse>(`audit`, { query });
      setItems(data.items);
      setTotal(data.total);
    } catch (err: any) {
      setToast({ message: err?.message || "Failed to load audit logs", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [api, page, entityType, since, search]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  // Reset to page 1 when filters change
  const handleEntityTypeChange = (val: string) => {
    setEntityType(val);
    setPage(1);
  };
  const handleSinceChange = (val: string) => {
    setSince(val);
    setPage(1);
  };
  const handleSearchChange = (val: string) => {
    setSearch(val);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={styles["page-shell"]}>
      {/* Filters card */}
      <div className={styles["audit-card"]}>
        <div className={styles["audit-card-header"]}>
          <h2 className={styles["audit-card-header-title"]}>Filters</h2>
        </div>
        <div className={styles["audit-card-body"]}>
          <div className={styles["filter-bar"]}>
            <div className={styles["audit-filter-field"]}>
              <label className={styles["audit-label"]}>Entity Type</label>
              <select
                value={entityType}
                onChange={(e) => handleEntityTypeChange(e.target.value)}
                aria-label="Entity type filter"
                className={styles["audit-control"]}
              >
                {ENTITY_TYPES.map((et) => (
                  <option key={et.value} value={et.value}>
                    {et.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles["audit-filter-field"]}>
              <label className={styles["audit-label"]}>Since</label>
              <input
                type="date"
                value={since}
                onChange={(e) => handleSinceChange(e.target.value)}
                aria-label="Since date"
                className={styles["audit-control"]}
              />
            </div>

            <div className={styles["audit-filter-field"]}>
              <label className={styles["audit-label"]}>Entity ID</label>
              <input
                type="text"
                placeholder="Search by Entity ID..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                aria-label="Search by entity ID"
                className={styles["audit-control"]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading && (
        <div className={styles["audit-card"]}>
          <div className={styles["audit-card-header"]}>
            <h2 className={styles["audit-card-header-title"]}>Loading…</h2>
          </div>
          <div className={styles["audit-card-body--flush"]}>
            <div className={styles["table-wrap"]}>
              <table className={styles["audit-table"]}>
                <thead>
                  <tr>
                    <th>Timestamp</th><th>Entity Type</th><th>Entity</th>
                    <th>Action</th><th>User</th><th>Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3].map((i) => (
                    <tr key={i} style={{ opacity: 0.5 }}>
                      <td><span className={styles["audit-skeleton"]} style={{ width: 120, height: 13 }} /></td>
                      <td><span className={styles["audit-skeleton"]} style={{ width: 80, height: 13 }} /></td>
                      <td><span className={styles["audit-skeleton"]} style={{ width: 100, height: 13 }} /></td>
                      <td><span className={styles["audit-skeleton"]} style={{ width: 60, height: 13 }} /></td>
                      <td><span className={styles["audit-skeleton"]} style={{ width: 90, height: 13 }} /></td>
                      <td><span className={styles["audit-skeleton"]} style={{ width: 140, height: 13 }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className={styles["audit-card"]}>
          <div className={styles["audit-card-header"]}>
            <h2 className={styles["audit-card-header-title"]}>Audit Logs</h2>
          </div>
          <div className={styles["audit-card-body--flush"]}>
            <div className={styles["audit-empty"]}>
              <svg className={styles["audit-empty__icon"]} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="8" y="6" width="32" height="36" rx="4" stroke="#d1d5db" strokeWidth="1.5" />
                <path d="M16 16h16M16 22h12M16 28h8" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <div className={styles["audit-empty__title"]}>No Audit Entries</div>
              <div className={styles["audit-empty__subtitle"]}>No log entries match your current filters.</div>
            </div>
          </div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className={styles["audit-card"]}>
          <div className={styles["audit-card-header"]}>
            <h2 className={styles["audit-card-header-title"]}>Audit Logs</h2>
          </div>
          <div className={styles["audit-card-body--flush"]}>
            <div className={styles["table-wrap"]}>
              <table className={styles["audit-table"]}>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Entity Type</th>
                    <th>Entity</th>
                    <th>Action</th>
                    <th>User</th>
                    <th>Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const changeSummaries = item.changeSummary ? null : parseChanges(item.changes);
                    return (
                      <tr key={item.id} data-testid={`audit-row-${item.id}`}>
                        <td>{formatTimestamp(item.timestamp)}</td>
                        <td>{humanizeIdentifier(item.entityType)}</td>
                        <td>{item.entityLabel || item.entityName || (item.entityId ? maskInternalValue(item.entityId) : "—")}</td>
                        <td>
                          <span
                            className={`${styles["action-badge"]} ${actionClass(item.action)}`}
                          >
                            {item.actionLabel || item.action || "—"}
                          </span>
                        </td>
                        <td>{item.actorLabel || item.userName || (item.userId ? `User ${maskInternalValue(item.userId)}` : "Unknown user")}</td>
                        <td>
                          {item.changeSummary ? (
                            <span>{item.changeSummary}</span>
                          ) : changeSummaries ? (
                            <ul className={styles["changes-list"]}>
                              {changeSummaries.map((c, i) => (
                                <li key={i}>
                                  <span className={styles["field-name"]}>
                                    {c.field}:
                                  </span>{" "}
                                  {c.from
                                    ? `${c.from} → ${c.to}`
                                    : c.to}
                                </li>
                              ))}
                            </ul>
                          ) : item.changes ? (
                            <details className={styles["technical-details"]}>
                              <summary>Details</summary>
                              <span className={styles["raw-changes"]}>
                                {maskInternalValue(item.changes)}
                              </span>
                            </details>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={styles["pagination"]}>
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span className={styles["page-info"]}>
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

const AuditHistory = () => {
  const authRequest = { ...loginRequest };

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
    >
        <div className={styles["audit-shell"]}>
          <FeatureGate
            feature="audit_history"
            fallback={
              <div className={styles["feature-fallback"]}>
                Audit History is not available on your current plan.
              </div>
            }
          >
            <AuditHistoryContent />
          </FeatureGate>
        </div>
    </MsalAuthenticationTemplate>
  );
};

export default AuditHistory;
